/**
 * Environment utilities for analytics packages.
 * These functions determine whether analytics services should be enabled
 * based on environment variables.
 */

/**
 * Check if Google Analytics should be enabled.
 * Google Analytics only works when VERCEL_ENV=production.
 */
export function isGAEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") return false;
  if (!process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID) return false;

  return true;
}

/**
 * Check if PostHog should be enabled.
 * - Always disabled when no API key is set.
 * - Enabled in all deployed environments (production, preview, staging).
 * - In local development (NODE_ENV=development), enabled only when
 *   NEXT_PUBLIC_POSTHOG_ENABLE_DEV=true is set, so developers can test
 *   tracking locally without polluting production data.
 */
export function isPostHogEnabled(): boolean {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return false;
  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_POSTHOG_ENABLE_DEV !== "true"
  ) {
    return false;
  }

  return true;
}

/**
 * Get the current PostHog environment name.
 */
export function getPostHogEnvironment(): string {
  return process.env.NEXT_PUBLIC_VERCEL_ENV ?? "development";
}

/**
 * Check if the Meta (Facebook) Pixel should be enabled.
 * Meta Pixel fires in production, and in local development only when explicitly
 * enabled for an isolated test setup. Preview deployments stay disabled.
 */
export function isMetaPixelEnabled(): boolean {
  if (!process.env.NEXT_PUBLIC_META_PIXEL_ID) return false;
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") return true;
  if (process.env.NEXT_PUBLIC_VERCEL_ENV) return false;

  return (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_META_PIXEL_ENABLE_DEV === "true"
  );
}

/**
 * Check if the Meta Conversions API (server-side) should be enabled.
 * Requires the same pixel ID as the browser pixel plus a CAPI access token,
 * so it stays a no-op until that token is configured.
 */
export function isMetaCapiEnabled(): boolean {
  if (!isMetaPixelEnabled()) return false;
  if (!process.env.META_CAPI_ACCESS_TOKEN) return false;

  return true;
}

/**
 * Check if Google Ads conversion tracking should be enabled.
 * Google Ads conversions only fire when VERCEL_ENV=production.
 */
export function isGoogleAdsEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") return false;
  if (!process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_SEND_TO) return false;

  return true;
}
